import { useEffect } from 'react'

import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetServerSidePropsType,
  InferGetStaticPropsType,
} from 'next'
import { useRouter } from 'next/router'

import commerce from '@lib/api/commerce'
import { Layout } from '@components/common'
import { LoreTwoProductView } from '@components/product'
import { getPageContext } from '@drf/getPageContext'
import { LoreTwo as ProductPageType } from '@generated/model/models'
import { getGlobalContentContext } from '@drf/getGlobalContentContext'
import { useGlobalContentContext } from '@context/GlobalContentContext'

export async function getServerSideProps({
  params,
  locale,
  locales,
  preview,
}: GetStaticPropsContext<{ slug: string }>) {
  const config = { locale, locales }
  const pagesPromise = commerce.getAllPages({ config, preview })
  const siteInfoPromise = commerce.getSiteInfo({ config, preview })
  const productPromise = commerce.getProduct({
    variables: { slug: 'loretwo' },
    config,
    preview,
  })

  const allProductsPromise = commerce.getAllProducts({
    variables: { first: 5 },
    config,
    preview,
  })

  const { pages } = await pagesPromise
  const { categories } = await siteInfoPromise
  const { product } = await productPromise

  const { products: relatedProducts } = await allProductsPromise

  if (!product) {
    throw new Error(`Product with slug _loretwo_ not found`)
  }

  const global = await getGlobalContentContext()
  const page: ProductPageType = await getPageContext(
    'loretwo-products/' + product.id
  )
  const homepage = await getPageContext('homepage')
  const loreTwoPage = await getPageContext('loretwopage')
  const howSection = loreTwoPage.how_section
  const faqblock = loreTwoPage.faqblock
  const fittersBlock = await getPageContext('findfittersblock')
  const fittersMap = await getPageContext('fitters')
  const fitters = Object.keys(fittersMap).map((i) => fittersMap[i])

  return {
    props: {
      pages,
      global,
      product,
      relatedProducts,
      categories,
      page,
      faqblock,
      howSection,
      fittersBlock,
      fitters,
    },
  }
}

// export async function getStaticPaths({ locales }: GetStaticPathsContext) {
//   const config = { locales }
//   const productPromise = commerce.getProduct({
//     variables: { slug: 'loretwo' },
//     config,
//     preview: true,
//   })

//   const { product } = await productPromise

//   const slugPaths = product?.variants
//     .filter((v) => v.defaultImage)
//     .map(
//       (v) =>
//         `/product/loretwo/${v.options[0].values[0].label.toLowerCase()}/${v.options[1].values[0].label.toLowerCase()}/${v.options[2].values[0].label.toLowerCase()}/${v.options[3].values[0].label.toLowerCase()}`
//     )

//   const paths = slugPaths ? ['/product/loretwo/', ...slugPaths] : ['/']

//   return {
//     paths: paths,
//     fallback: 'blocking',
//   }
// }

export default function LoreTwoPage({
  product,
  page,
  global,
  faqblock,
  howSection,
  fittersBlock,
  fitters,
  relatedProducts,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter()

  const { setData } = useGlobalContentContext()

  useEffect(() => {
    const data = {
      ...global,
      loretwo_product: product,
    }
    setData(data)
  }, [global, setData])

  return router.isFallback ? (
    <h1>Loading...</h1>
  ) : (
    <LoreTwoProductView
      product={product}
      relatedProducts={relatedProducts}
      page={page}
      howSection={howSection}
      fittersSection={fittersBlock}
      fitters={fitters}
      faqblock={faqblock}
    />
  )
}

LoreTwoPage.Layout = Layout
